import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { API } from "@/App";
import { CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const emailParam = params.get("email") || "";
  const tid = params.get("tid") || "";

  const [status, setStatus] = useState("idle"); // idle | loading | done | error | not_us
  const [reason, setReason] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [email, setEmail] = useState(emailParam);

  // On mount, if we have an email, do a one-click unsubscribe automatically.
  useEffect(() => {
    if (!emailParam) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailParam)) {
      setStatus("error");
      setErrMsg("Invalid email address in link");
      return;
    }
    setStatus("loading");
    fetch(`${API}/marketing/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailParam, tid }),
    })
      .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body?.detail || "Unsubscribe failed");
        setStatus("done");
      })
      .catch((e) => {
        setStatus("error");
        setErrMsg(String(e.message || e));
      });
  }, [emailParam, tid]);

  const submitReason = async (r) => {
    setReason(r);
    await fetch(`${API}/marketing/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, tid, reason: r }),
    });
  };

  const manual = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrMsg("Enter a valid email address");
      return;
    }
    setStatus("loading");
    try {
      const r = await fetch(`${API}/marketing/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, tid }),
      });
      if (!r.ok) throw new Error("Unsubscribe failed");
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setErrMsg(String(err.message || err));
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12" data-testid="unsub-page">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden">
        <div className="bg-slate-900 px-6 py-5 text-white">
          <p className="text-[10px] font-bold uppercase tracking-[2px] text-amber-400 mb-1">Sterile Mate LLC</p>
          <h1 className="text-xl font-black">Unsubscribe from marketing emails</h1>
        </div>

        <div className="p-6 space-y-4">
          {status === "loading" && (
            <div className="flex items-center gap-3 text-slate-600" data-testid="unsub-loading">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Processing your unsubscribe request…</span>
            </div>
          )}

          {status === "done" && (
            <div className="space-y-4" data-testid="unsub-success">
              <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-emerald-900">You&apos;re unsubscribed.</p>
                  <p className="text-sm text-emerald-800 mt-1">
                    <span className="font-mono text-xs bg-white px-2 py-0.5 rounded border border-emerald-200">{email}</span> will no longer receive marketing emails from Sterile Mate.
                  </p>
                  <p className="text-xs text-emerald-700 mt-2">Order confirmations, RFQ replies, and quote responses (transactional) will continue.</p>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <p className="text-sm font-semibold text-slate-700 mb-2">Mind sharing why? (optional)</p>
                <div className="flex flex-wrap gap-2">
                  {["Too many emails", "Not relevant", "Never signed up", "Other"].map((r) => (
                    <button
                      key={r}
                      onClick={() => submitReason(r)}
                      disabled={reason === r}
                      data-testid={`unsub-reason-${r.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition ${
                        reason === r
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                      }`}
                    >
                      {reason === r ? "Thanks!" : r}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 flex flex-col gap-2">
                <Link
                  to={`/preferences${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                  data-testid="unsub-manage-prefs-link"
                  className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 font-semibold"
                >
                  Rather choose specific topics? Manage preferences <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/" className="text-xs text-slate-500 hover:text-slate-700">Back to sterilemate.com</Link>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 rounded-lg" data-testid="unsub-error">
              <XCircle className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-rose-900">Something went wrong.</p>
                <p className="text-sm text-rose-800 mt-1">{errMsg || "Please try again in a moment."}</p>
                <a href="mailto:unsubscribe@sterilemate.com?subject=Unsubscribe" className="mt-2 inline-block text-xs text-rose-700 underline">
                  Email us instead: unsubscribe@sterilemate.com
                </a>
              </div>
            </div>
          )}

          {status === "idle" && !emailParam && (
            <form onSubmit={manual} className="space-y-3" data-testid="unsub-manual-form">
              <p className="text-sm text-slate-600">Enter your email to unsubscribe from all Sterile Mate marketing emails.</p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourfacility.com"
                required
                data-testid="unsub-email-input"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <button
                type="submit"
                data-testid="unsub-submit-btn"
                className="w-full px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-lg transition"
              >
                Unsubscribe from all marketing
              </button>
            </form>
          )}
        </div>

        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 text-xs text-slate-500 leading-relaxed">
          Sterile Mate LLC · 396 W Greens Road, Suite 1139 · Houston, TX 77067 · USA · <a href="tel:8009883892" className="underline">800.988.3892</a>
        </div>
      </div>
    </div>
  );
}
